import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API}/business-info`);
        setBusinessInfo(response.data);
      } catch (error) {
        console.error("Error fetching business info:", error);
      }
    };
    fetchBusinessInfo();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-section-alt"
      data-testid="contact-section"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <span className="inline-block px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-4">
              Contact Us
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Get In Touch
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Have questions about our cleaning services? We're here to help. 
              Reach out to us and our team will respond promptly.
            </p>

            {businessInfo && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-4">Business Hours</h4>
                  <div className="space-y-2 text-slate-600">
                    <p className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span className="font-medium text-slate-800">{businessInfo.hours.monday_friday}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Saturday:</span>
                      <span className="font-medium text-slate-800">{businessInfo.hours.saturday}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-medium text-slate-800">{businessInfo.hours.sunday}</span>
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-4">Contact Information</h4>
                  <div className="space-y-3 text-slate-600">
                    <p>
                      <span className="text-slate-500">Phone:</span>{" "}
                      <a href={`tel:${businessInfo.phone}`} className="text-sky-600 hover:underline font-medium">
                        {businessInfo.phone}
                      </a>
                    </p>
                    <p>
                      <span className="text-slate-500">Email:</span>{" "}
                      <a href={`mailto:${businessInfo.email}`} className="text-sky-600 hover:underline font-medium">
                        {businessInfo.email}
                      </a>
                    </p>
                    <p>
                      <span className="text-slate-500">Address:</span>{" "}
                      <span className="font-medium text-slate-800">{businessInfo.address}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="contact-name" className="form-label">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact-name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="form-input"
                  required
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <Label htmlFor="contact-email" className="form-label">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="form-input"
                  required
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <Label htmlFor="contact-phone" className="form-label">
                  Phone Number
                </Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="form-input"
                  data-testid="contact-input-phone"
                />
              </div>

              <div>
                <Label htmlFor="contact-message" className="form-label">
                  Message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="contact-message"
                  placeholder="How can we help you?"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  className="form-input min-h-[120px]"
                  required
                  data-testid="contact-input-message"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-4 font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
